import { useState, useEffect } from 'react';
import { Download, BarChart3, TrendingUp } from 'lucide-react';
import api from '@/utils/api';
import { formatGs } from '@/utils/helpers';

export default function ReportsPage() {
  const [orders, setOrders] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().split('T')[0]; });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/pedidos?limit=1000'),
      api.get('/sucursales'),
    ]).then(([o, b]) => {
      setOrders(o.data.orders || []);
      setBranches(b.data.sucursales || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Filter by date range
  const filtered = orders.filter(o => {
    const d = o.created_at?.split('T')[0];
    return d >= dateFrom && d <= dateTo && o.status !== 'cancelado' && o.status !== 'rechazado';
  });

  // Aggregations
  const totalVentas = filtered.reduce((s, o) => s + parseFloat(o.total || 0), 0);
  const totalPedidos = filtered.length;

  const ventasPorSucursal = {};
  filtered.forEach(o => {
    const key = o.branch_name || `Sucursal #${o.branch_id}`;
    if (!ventasPorSucursal[key]) ventasPorSucursal[key] = { total: 0, count: 0 };
    ventasPorSucursal[key].total += parseFloat(o.total || 0);
    ventasPorSucursal[key].count++;
  });

  // Most sold products - need order detail but we only have summary from list endpoint
  // We'll show branch breakdown as the main report

  const exportCSV = () => {
    const header = 'ID,Fecha,Cliente,Sucursal,Total,Estado,Metodo Pago\n';
    const rows = filtered.map(o =>
      `${o.id},${o.created_at?.split('T')[0]},${o.customer_name},${o.branch_name},${o.total},${o.status},${o.metodo_pago}`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte_ventas_${dateFrom}_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold">Reportes</h2>
          <p className="text-sm text-zinc-500">Resumen de ventas</p>
        </div>
        <button onClick={exportCSV} className="flex items-center gap-2 bg-zinc-800 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-zinc-700 transition-colors" data-testid="export-csv-btn">
          <Download size={16} /> Exportar CSV
        </button>
      </div>

      {/* Date filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <label className="text-xs text-zinc-500">Desde</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-1.5 text-sm text-zinc-300 outline-none" data-testid="report-date-from" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-zinc-500">Hasta</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-1.5 text-sm text-zinc-300 outline-none" data-testid="report-date-to" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#E84C1C] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-zinc-500 text-xs mb-1"><BarChart3 size={14} /> Ventas totales</div>
              <p className="text-2xl font-bold text-[#E84C1C]">{formatGs(totalVentas)}</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-zinc-500 text-xs mb-1"><TrendingUp size={14} /> Pedidos completados</div>
              <p className="text-2xl font-bold">{totalPedidos}</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-zinc-500 text-xs mb-1"><TrendingUp size={14} /> Ticket promedio</div>
              <p className="text-2xl font-bold">{totalPedidos > 0 ? formatGs(totalVentas / totalPedidos) : 'Gs. 0'}</p>
            </div>
          </div>

          {/* Sales by branch */}
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl overflow-hidden mb-6">
            <h3 className="text-sm font-semibold p-4 pb-2 text-zinc-300">Ventas por Sucursal</h3>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-zinc-800/50 text-zinc-500 text-xs uppercase tracking-wider">
                <th className="text-left p-3 font-medium">Sucursal</th><th className="text-right p-3 font-medium">Pedidos</th><th className="text-right p-3 font-medium">Total</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {Object.entries(ventasPorSucursal).length === 0 ? (
                  <tr><td colSpan={4} className="p-6 text-center text-zinc-500">No hay datos para el rango seleccionado.</td></tr>
                ) : (
                  Object.entries(ventasPorSucursal).sort((a, b) => b[1].total - a[1].total).map(([name, data]) => {
                    const pct = totalVentas > 0 ? (data.total / totalVentas) * 100 : 0;
                    return (
                      <tr key={name} className="border-b border-zinc-800/30">
                        <td className="p-3 font-medium">{name}</td>
                        <td className="p-3 text-right text-zinc-400">{data.count}</td>
                        <td className="p-3 text-right font-medium text-[#E84C1C]">{formatGs(data.total)}</td>
                        <td className="p-3 w-32"><div className="h-2 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-[#E84C1C] rounded-full" style={{ width: `${pct}%` }} /></div></td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Recent orders table */}
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl overflow-hidden">
            <h3 className="text-sm font-semibold p-4 pb-2 text-zinc-300">Ultimos pedidos del periodo</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-800/50 text-zinc-500 text-xs uppercase tracking-wider">
                  <th className="text-left p-3 font-medium">#</th><th className="text-left p-3 font-medium">Cliente</th><th className="text-left p-3 font-medium">Sucursal</th><th className="text-right p-3 font-medium">Total</th><th className="text-left p-3 font-medium">Estado</th>
                </tr></thead>
                <tbody>
                  {filtered.slice(0, 20).map(o => (
                    <tr key={o.id} className="border-b border-zinc-800/30">
                      <td className="p-3 text-[#E84C1C] font-mono">#{o.id}</td>
                      <td className="p-3 text-zinc-300">{o.customer_name}</td>
                      <td className="p-3 text-zinc-400 text-xs">{o.branch_name}</td>
                      <td className="p-3 text-right font-medium">{formatGs(o.total)}</td>
                      <td className="p-3 text-xs text-zinc-400">{o.status}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-zinc-500">Sin pedidos en este periodo.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
